import { BackGLBInfo, BackMorphsData } from '@types'
import { logDimFlow } from '@utils/debug/dimensionFlowDiagnostic'

export default function getMorphsFromGLB(backGLBInfo: BackGLBInfo) {
  const morphs = backGLBInfo?.morphs
  if (morphs) {
    const foundMorphs = new Set(['Alto', 'Ancho', 'Fondo'])
    const morphed = morphs.map((el: BackMorphsData) => {
      foundMorphs.delete(el.nombre)
      return {
        default: el.valor,
        _default: el.valor,
        morphId: el.idMorph,
        order: el.orden,
        type: el.tipo,
        min: el.valorMin,
        max: el.valorMax,
        name: el.nombre,
        show: true,
        modifyable: true,
      }
    })

    // Agregar morph de Regleta si existe en backGLBInfo
    const hasRegleta =
      typeof backGLBInfo.regleta === 'number' &&
      typeof backGLBInfo.regletaMinimo === 'number' &&
      typeof backGLBInfo.regletaMaximo === 'number'

    if (hasRegleta) {
      morphed.push({
        default: backGLBInfo.regleta,
        _default: backGLBInfo.regleta,
        morphId: -1, // ID especial para regleta
        order: 999, // Al final
        type: 'R',
        min: backGLBInfo.regletaMinimo,
        max: backGLBInfo.regletaMaximo,
        name: 'Regleta',
        show: true,
        modifyable: true,
        reversed: false,
      } as (typeof morphed)[number] & { reversed?: boolean })
    }

    // Solo comprobar Ancho/Alto/Fondo: Regleta (y otros) no deben invalidar el set.
    const dimensionMorphs = morphed.filter(
      (el) => el.name === 'Ancho' || el.name === 'Alto' || el.name === 'Fondo',
    )
    const hasAllDimensions =
      dimensionMorphs.length >= 3 &&
      dimensionMorphs.every(
        (el) => el.name === 'Ancho' || el.name === 'Alto' || el.name === 'Fondo',
      )
    const result = hasAllDimensions
      ? morphed
      : morphed.concat(
          Array.from(foundMorphs).map((el) => ({
            default: (backGLBInfo as any)[el.toLowerCase()] as number,
            _default: (backGLBInfo as any)[el.toLowerCase()] as number,
            morphId: 0,
            order: 0,
            type: '',
            min: (backGLBInfo as any)[el.toLowerCase()] as number,
            max: (backGLBInfo as any)[el.toLowerCase()] as number,
            show: true,
            name: el,
            modifyable: false,
          })),
        )
    logDimFlow({
      file: 'store/getMorphsFromGLB.ts',
      fn: 'getMorphsFromGLB',
      phase: 'morphs.parse',
      reason:
        'Morphs leídos desde API backGLBInfo.morphs (valor/min/max). default=_default=el.valor inicial.',
      snapshot: {
        catalogID: (backGLBInfo as { codigo?: string }).codigo,
        description: backGLBInfo.descripcion,
        tipoNormalizado: backGLBInfo.tipoNormalizado,
        catalogHeaderMm: {
          ancho: backGLBInfo.ancho,
          alto: backGLBInfo.alto,
          fondo: backGLBInfo.fondo,
        },
        morphsAvailable: result.map((m) => ({
          name: m.name,
          min: m.min,
          max: m.max,
          default: m.default,
          _default: m._default,
          modifyable: m.modifyable,
        })),
        usesMorph: true,
        usesScale: false,
        extra: { source: 'api.morphs', count: result.length },
      },
    })
    return result
  } else {
    const synthetic = [
      {
        default: backGLBInfo.ancho ?? 0,
        _default: backGLBInfo.ancho ?? 0,
        morphId: 0,
        order: 0,
        min: backGLBInfo.ancho ?? 0,
        max: backGLBInfo.ancho ?? 0,
        name: 'Ancho',
        show: true,
        type: '',
        modifyable: Boolean(backGLBInfo.modificarAncho),
      },
      {
        default: backGLBInfo.alto ?? 0,
        _default: backGLBInfo.alto ?? 0,
        morphId: 0,
        order: 0,
        min: backGLBInfo.alto ?? 0,
        max: backGLBInfo.alto ?? 0,
        name: 'Alto',
        show: true,
        type: '',
        modifyable: Boolean(backGLBInfo.modificarAlto),
      },
      {
        default: backGLBInfo.fondo ?? 0,
        _default: backGLBInfo.fondo ?? 0,
        morphId: 0,
        order: 0,
        min: backGLBInfo.fondo ?? 0,
        max: backGLBInfo.fondo ?? 0,
        name: 'Fondo',
        show: true,
        type: '',
        modifyable: Boolean(backGLBInfo.modificarFondo),
      },
    ]
    if (
      typeof backGLBInfo.regleta === 'number' &&
      typeof backGLBInfo.regletaMinimo === 'number' &&
      typeof backGLBInfo.regletaMaximo === 'number'
    ) {
      synthetic.push({
        default: backGLBInfo.regleta as number,
        _default: backGLBInfo.regleta as number,
        morphId: -1,
        order: 999,
        type: 'R',
        min: backGLBInfo.regletaMinimo as number,
        max: backGLBInfo.regletaMaximo as number,
        name: 'Regleta',
        show: true,
        modifyable: true,
        reversed: false,
      } as (typeof synthetic)[number] & { reversed?: boolean })
    }
    logDimFlow({
      file: 'store/getMorphsFromGLB.ts',
      fn: 'getMorphsFromGLB',
      phase: 'morphs.parse',
      reason:
        'API morphs=null → morphs sintéticos desde cabecera ancho/alto/fondo; modifyable desde modificarAncho/Alto/Fondo. Candidato a free-dim o escala universal.',
      snapshot: {
        catalogID: (backGLBInfo as { codigo?: string }).codigo,
        description: backGLBInfo.descripcion,
        tipoNormalizado: backGLBInfo.tipoNormalizado,
        catalogHeaderMm: {
          ancho: backGLBInfo.ancho,
          alto: backGLBInfo.alto,
          fondo: backGLBInfo.fondo,
        },
        morphsAvailable: synthetic.map((m) => ({
          name: m.name,
          min: m.min,
          max: m.max,
          default: m.default,
          _default: m._default,
          modifyable: m.modifyable,
        })),
        usesMorph: false,
        usesScale: true,
        extra: {
          source: 'catalog.header.synthetic',
          modificarAncho: backGLBInfo.modificarAncho,
          modificarAlto: backGLBInfo.modificarAlto,
          modificarFondo: backGLBInfo.modificarFondo,
        },
      },
    })
    return synthetic
  }
}
